import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import * as settings from '../api/settings';
import type { SettingsUser, UpdateUserInput } from '../api/settings';

interface CompanyOption {
  id: string;
  displayName: string;
  color: string;
}

interface Props {
  user: SettingsUser | null; // null = closed
  users: SettingsUser[];
  companies: CompanyOption[];
  onClose: () => void;
  onDone: (toastMessage: string) => Promise<void> | void;
}

function parseList(value: string): string[] {
  return value
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/**
 * Edit user modal. Email is read-only (it lives in Cognito and isn't part
 * of the profile record). Other fields diff-send — only changed keys are
 * included in the PATCH so we don't accidentally clobber unrelated state.
 *
 * `personalSharedWith` and `companyIds` are now pre-populated from
 * `user.personalSharedWith` / `user.companies` (backend returns both on
 * `listUsers`). Both use REPLACE semantics server-side; the client sends
 * the full desired set when the admin edits them.
 */
export function EditUserModal({ user, users, companies, onClose, onDone }: Props) {
  const [displayName, setDisplayName] = useState('');
  const [handlesText, setHandlesText] = useState('');
  const [addressesText, setAddressesText] = useState('');
  const [color, setColor] = useState('#0ea5c9');
  const [personalShared, setPersonalShared] = useState<string[]>([]);
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!user) return;
    setDisplayName(user.displayName);
    setHandlesText(user.handles.join(', '));
    setAddressesText(user.knownAddresses.join(', '));
    setColor(user.color);
    setPersonalShared(user.personalSharedWith ?? []);
    setSelectedCompanies(user.companies ?? []);
    setErr(null);
    setTimeout(() => nameRef.current?.focus(), 0);
  }, [user]);

  const canSubmit = useMemo(
    () => !!user && displayName.trim().length > 0 && !busy,
    [user, displayName, busy]
  );

  // Also filter out the user being edited from the "shares personal with"
  // picker — sharing your own personal with yourself is a no-op.
  const shareableUsers = useMemo(
    () => (user ? users.filter((u) => u.userId !== user.userId) : users),
    [users, user]
  );

  if (!user) return null;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!user || !canSubmit) return;
    setBusy(true);
    setErr(null);
    try {
      const patch: UpdateUserInput = {};
      const newName = displayName.trim();
      if (newName !== user.displayName) patch.displayName = newName;
      const newHandles = parseList(handlesText);
      if (JSON.stringify(newHandles) !== JSON.stringify(user.handles)) {
        patch.handles = newHandles;
      }
      const newAddrs = parseList(addressesText).map((a) => a.toLowerCase());
      if (JSON.stringify(newAddrs) !== JSON.stringify(user.knownAddresses)) {
        patch.knownAddresses = newAddrs;
      }
      if (color !== user.color) patch.color = color;

      const sameShare =
        JSON.stringify([...personalShared].sort()) ===
        JSON.stringify([...(user.personalSharedWith ?? [])].sort());
      if (!sameShare) patch.personalSharedWith = personalShared;

      const sameCompanies =
        JSON.stringify([...selectedCompanies].sort()) ===
        JSON.stringify([...(user.companies ?? [])].sort());
      if (!sameCompanies) patch.companyIds = selectedCompanies;

      if (Object.keys(patch).length === 0) {
        onClose();
        return;
      }

      await settings.updateUser(user.userId, patch);
      await onDone(`Saved ${newName}`);
    } catch (ex) {
      setErr((ex as Error).message);
    } finally {
      setBusy(false);
    }
  }

  function togglePersonal(sub: string) {
    setPersonalShared((prev) =>
      prev.includes(sub) ? prev.filter((x) => x !== sub) : [...prev, sub]
    );
  }

  function toggleCompany(id: string) {
    setSelectedCompanies((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }

  return (
    <div
      className="new-task-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={`Edit ${user.displayName}`}
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <form
        className="new-task-dialog settings-modal"
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
      >
        <header>
          <h3>Edit {user.displayName}</h3>
          <button type="button" onClick={onClose} aria-label="Close">×</button>
        </header>

        <label>
          <span>User ID (Cognito sub)</span>
          <input type="text" value={user.userId} disabled readOnly />
        </label>

        <label>
          <span>Display name</span>
          <input
            ref={nameRef}
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            required
          />
        </label>

        <div className="new-task-grid">
          <label>
            <span>Handles</span>
            <input
              type="text"
              value={handlesText}
              onChange={(e) => setHandlesText(e.target.value)}
              placeholder="jane, j, jd"
            />
          </label>
          <label>
            <span>Known addresses</span>
            <input
              type="text"
              value={addressesText}
              onChange={(e) => setAddressesText(e.target.value)}
              placeholder="jane@x.com, j@y.com"
            />
          </label>
          <label>
            <span>Color</span>
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
            />
          </label>
        </div>

        {companies.length > 0 && (
          <div className="settings-multiselect">
            <span className="settings-field-label">Companies</span>
            <div className="settings-chip-row">
              {companies.map((c) => {
                const selected = selectedCompanies.includes(c.id);
                return (
                  <button
                    key={c.id}
                    type="button"
                    className={`settings-chip-button ${selected ? 'is-selected' : ''}`}
                    onClick={() => toggleCompany(c.id)}
                  >
                    <span
                      className="settings-color-dot"
                      style={{ backgroundColor: c.color }}
                      aria-hidden="true"
                    />
                    {c.displayName}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {shareableUsers.length > 0 && (
          <div className="settings-multiselect">
            <span className="settings-field-label">Shares personal with</span>
            <div className="settings-chip-row">
              {shareableUsers.map((u) => {
                const selected = personalShared.includes(u.userId);
                return (
                  <button
                    key={u.userId}
                    type="button"
                    className={`settings-chip-button ${selected ? 'is-selected' : ''}`}
                    onClick={() => togglePersonal(u.userId)}
                  >
                    <span
                      className="settings-color-dot"
                      style={{ backgroundColor: u.color }}
                      aria-hidden="true"
                    />
                    {u.displayName}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {err && <div className="settings-error">{err}</div>}

        <div className="new-task-footer">
          <span className="muted small">Changes apply immediately.</span>
          <button type="submit" className="primary" disabled={!canSubmit}>
            {busy ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
    </div>
  );
}
